/*
  The A-Z index of a game's sets (the third display mode, beside the card grid and the
  table).

  Modelled on admin's SetsAlphabeticalGrid — a fixed letter strip that scrolls to a
  section, then letter groups in columns, each a list of set rows with the count on the
  right — minus admin's buttons (Edit, Add a Set, Export CSV, Finish Manager), which is
  what Robert asked for and what a public page has no business showing. Admin's hover
  preview card goes with them: it exists there to save an editor a page load, and here it
  would sit between a visitor and the link they are aiming at.

  It renders the same three states through the same resolveTableStatus as the grid and the
  table, so changing display mode cannot change "there is an error" into a blank page.

  Two things this mode depends on and does not own:

    - **The whole catalog is loaded** (D31). An index over a partial catalog is actively
      misleading: a letter reads as empty when its sets simply have not been fetched.
      GameView walks every page up front, which is what makes this honest.
    - **The rows arrive filtered** (§12.19). The search narrows this list exactly as it
      narrows the other two, and the letter counts fall with it, so a letter greys out
      when the current search has nothing under it.
*/

import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  DataTableEmpty,
  DataTableError,
  resolveTableStatus,
} from "components/DataTable";
import { CARD_TITLE_LINK, FOCUS_RING, TILE_CHIP } from "components/styles";
import { formatCount } from "lib";
import { buildLetterBar, groupSetsAlphabetically, letterSectionId } from "lib/setIndex";
import { sanitizeURIPathSegment } from "helpers/routing";

import { Set } from "types/gameInfo";

export interface SetsAlphabeticalProps {
  /* Already filtered and sorted by the view; grouping re-sorts within a letter. */
  data: readonly Set[];
  gameCode: string;
  isLoading?: boolean;
  /* RTK Query's error, straight through — same contract as DataTable's. */
  error?: unknown;
  /* The language chip, shown only in the Other bucket (pop-language-split D12). */
  showLanguage?: boolean;
}

/*
  Columns, not a grid: the home page's ALPHABETICAL_GRID (views/HomeView.tsx), so "All
  Sets" reads like "All TCGs" (Robert, 2026-10-20). A grid makes every letter in a row as
  tall as the longest one, which on Pokemon leaves whole screens of white under the short
  letters; columns size to their content. Each letter is `break-inside-avoid` so its
  heading never parts from its sets, and the flow runs down a column, as an index does.
*/
const GRID =
  "list-none columns-1 gap-x-10 p-0 sm:columns-2 lg:columns-3 xl:columns-4";

const LETTER_BUTTON_BASE =
  `skin-az-letter h-8 w-8 rounded-md text-sm font-semibold leading-none ${FOCUS_RING}`;

/*
  A letter with no sets is not hidden and not `disabled`: disabled takes it out of the tab
  order, so a keyboard user tabbing the strip would find its length changing under them as
  they type in the search box. It is inert and says so instead.
*/
const letterButtonClass = (enabled: boolean, highlighted: boolean): string => {
  if (!enabled) {
    return `${LETTER_BUTTON_BASE} cursor-default text-pcg-textDim`;
  }
  /*
    `skin-az-letter--on` is the gilded skin's hook for the active letter and styles nothing
    in navy. Gilded swaps the filled gold chip for an underline (src/skins/gilded.css) —
    the chip is marking a scroll position, not a selection, and it is the loudest thing on
    a page of set names.
  */
  return `${LETTER_BUTTON_BASE} ${
    highlighted
      ? "skin-az-letter--on bg-pcg-goldText text-pcg-page"
      : "text-pcg-text hover:bg-pcg-raised hover:text-pcg-goldText"
  }`;
};

const SetsAlphabetical = (props: SetsAlphabeticalProps) => {
  const { data, gameCode, isLoading = false, error, showLanguage = true } = props;

  /* Hooks first: the status branches below return early (rules-of-hooks). */
  const groups = useMemo(() => groupSetsAlphabetically(data), [data]);
  const letters = useMemo(() => buildLetterBar(groups), [groups]);

  const sectionRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const status = resolveTableStatus(isLoading, error, data.length);

  const scrollToLetter = (key: string) => {
    const section = sectionRefs.current[key];
    if (!section) {
      return;
    }
    /*
      Honoured rather than assumed: a smooth scroll is motion, and someone who has asked
      their system for less of it still needs to arrive at the letter.
    */
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    section.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    setHighlighted(key);
  };

  if (status === "loading") {
    return (
      <ul aria-hidden="true" className={GRID}>
        {Array.from({ length: 4 }).map((_unused, index) => (
          <li
            key={`alphabetical-skeleton-${index}`}
            className="mb-6 flex break-inside-avoid flex-col gap-2"
          >
            <span className="h-5 w-8 animate-pulse rounded bg-pcg-raised" />
            <span className="h-4 w-full animate-pulse rounded bg-pcg-raised" />
            <span className="h-4 w-2/3 animate-pulse rounded bg-pcg-raised" />
          </li>
        ))}
      </ul>
    );
  }

  if (status === "error") {
    return <DataTableError />;
  }

  if (status === "empty") {
    return <DataTableEmpty title="No sets found" description="No set matches this search." />;
  }

  return (
    <div className="flex flex-col gap-6">
      {/*
        A group, not a nav: these move the page rather than change it, and a landmark for
        26 in-page jumps would sit in the landmark list next to the real ones.
      */}
      <div
        role="group"
        aria-label="Jump to letter"
        /*
          Centred while it wraps, spread once it fits. `justify-between` on a wrapped last
          row pushes its handful of letters out to both edges, so at 375px — where 27
          buttons take three rows — the strip would read as three unrelated groups.
        */
        className="skin-az flex flex-wrap justify-center gap-1 rounded-lg border border-pcg-border bg-pcg-card px-2 py-2 sm:justify-between sm:gap-y-1"
      >
        {letters.map((entry) => {
          const enabled = entry.count > 0;
          return (
            <button
              key={entry.key}
              type="button"
              onClick={() => scrollToLetter(entry.key)}
              aria-disabled={enabled ? undefined : true}
              aria-label={`${entry.key} — ${formatCount(entry.count)} ${
                entry.count === 1 ? "set" : "sets"
              }`}
              className={letterButtonClass(enabled, highlighted === entry.key)}
            >
              <span aria-hidden="true">{entry.key}</span>
            </button>
          );
        })}
      </div>

      <ul className={GRID}>
        {groups.map((group) => (
          <li
            key={group.key}
            id={letterSectionId(group.key)}
            ref={(element) => {
              sectionRefs.current[group.key] = element;
            }}
            className="mb-6 scroll-mt-6 break-inside-avoid"
          >
            <div className="mb-1 flex items-center gap-3">
              <h3 className="font-display text-lg font-bold leading-none text-pcg-goldText">
                {group.key}
              </h3>
              <span aria-hidden="true" className="skin-section-rule h-px flex-1 bg-pcg-border" />
            </div>
            <ul className="list-none p-0">
              {group.games.map((set) => (
                <li
                  key={set.id}
                  className="flex items-baseline justify-between gap-3 rounded-md px-1 py-1 hover:bg-pcg-raised"
                >
                  {/*
                    Name in page text with the language beside it, as admin's list does
                    (SetsAlphabeticalGrid.tsx:83-96). Sets are unique per (name, language),
                    so the same name repeats down a letter and the tag is what tells the
                    otherwise identical rows apart. Gold is kept for the letter heading and
                    for hover, rather than being spent on every one of 960 rows.
                  */}
                  <span className="flex min-w-0 items-baseline gap-2">
                    <Link
                      to={`/${gameCode}/${sanitizeURIPathSegment(set.setName)}`}
                      title={set.displayName ?? set.setName}
                      className={`truncate font-medium ${CARD_TITLE_LINK}`}
                    >
                      {set.displayName ?? set.setName}
                    </Link>
                    {showLanguage && set.languages && set.languages.length === 1 ? (
                      <span className={`${TILE_CHIP} shrink-0`}>{set.languages[0]}</span>
                    ) : null}
                  </span>
                  {/*
                    tabular-nums so the counts down a column line up on their digits, and
                    the column is the point of putting them on the right.
                  */}
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-pcg-goldText">
                    {formatCount(set.cardsGraded ?? 0)}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SetsAlphabetical;
